import { CircularProgress } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import axios, { AxiosError } from 'axios';
import { Formik } from 'formik';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useValidateSession } from '../../hooks';
import useStore from '../../store/store';
import { getFirstRouteForUsersPrivileges } from '../../store/storeUtils';
import { Store } from '../../store/types';
import { ErrorType } from '../../types/commonTypes';
import { LoginFormValues, SessionData } from '../../types/LogInTypes';
import API from '../../utils/API';
import { didApiCallFailDueToUnauthorizedUser, getErrorMessage } from '../../utils/apiUtils';
import { useUrlQuery } from '../../utils/commonUtils';
import { persistLogin } from '../../utils/localStorage';
import { DESTINATION } from '../../utils/loginUtil';
import { isObjectValidAndNotEmpty, isTextValidAndNotEmpty } from '../../utils/nullChecks';
import { getLoginFormValidation } from './loginContainerUtils';
import LoginForm from './LoginForm';

type Props = {};

const LoginContainer = (props: Props) => {
    console.log(props);
    const history = useHistory();
    const query = useUrlQuery();

    const appConfiguration = useStore((state: Store) => state.appConfiguration);

    const destination = query.get(DESTINATION) ||
        getFirstRouteForUsersPrivileges(
            ['BRANCHES'],
            appConfiguration,
        );

    // Authenticating user
    useValidateSession(destination);

    const [logInErrorMessage, setLogInErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showTwoFactorAuthCode, setShowTwoFactorAuthCode] = useState(false);

    const setSessionTimeout = useStore((state: Store) => state.setSessionTimeout);

    const redirectToDestination = () => {
        history.push(destination);
    };

    const login = async (values: LoginFormValues) => {
        try {
            setIsSubmitting(true);
            const loginResp = await axios.post(API.AUTH.LOGIN, values);
            persistLogin(loginResp.data);
            setSessionTimeout({ timeout: false });
            redirectToDestination();
        } catch (e) {
            const error = e as AxiosError<ErrorType>;
            console.log('error: ', error);
            if (didApiCallFailDueToUnauthorizedUser(error)) {
                setLogInErrorMessage('Authentication Failed');
            } else {
                const errorMessage = getErrorMessage(error);
                setLogInErrorMessage(errorMessage);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (values: LoginFormValues) => {
        if (isTextValidAndNotEmpty(values.twoFactorAuthCode)) {
            login(values);
            return;
        }
        try {
            setIsSubmitting(true);
            const response = await axios.post(API.AUTH.VALIDATE_USERNAME_AND_PASSWORD, values);
            if (isObjectValidAndNotEmpty<SessionData>(response.data)) {
                if (response.data.twoFactorAuthEnabled) {
                    setShowTwoFactorAuthCode(true);
                    setLogInErrorMessage('');
                } else {
                    login(values);
                }
            }
        } catch (e) {
            const error = e as AxiosError<ErrorType>;
            if (didApiCallFailDueToUnauthorizedUser(error)) {
                setLogInErrorMessage('Authentication Failed');
            } else {
                const errorMessage = getErrorMessage(error);
                setLogInErrorMessage(errorMessage);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Formik
                initialValues={{ username: '', password: '', twoFactorAuthCode: '' }}
                onSubmit={handleSubmit}
                validationSchema={getLoginFormValidation(showTwoFactorAuthCode)}
                validateOnChange={false}
                validateOnMount={false}
                validateOnBlur={false}
            >
                <LoginForm
                    logInErrorMessage={logInErrorMessage}
                    showTwoFactorAuthCode={showTwoFactorAuthCode}
                />
            </Formik>
            <Backdrop
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                open={isSubmitting}
            >
                <CircularProgress />
            </Backdrop>
        </>
    );
};

export default LoginContainer;
